import React from "react";
import { View, Text } from "../../shared/Themed";
import { formatValueAsDataType, renameLabel } from "../../../utils/common";
import { checkIsDate } from "../../../utils/dateUtility";
import { ReadOnlyProps } from "../../../utils/templates";
import { useTranslation } from "../../../utils/useTranslation";
import { useAppTheme } from "../../../hooks/useAppTheme";

interface Props {
  listData: any;
  excludedKeys: Array<string>;
  orderedKeys?: Array<string>;
  renameList?: Record<string, Record<string, string | boolean | number>>;
  specialLabels?: Record<string, Record<string, string>>;
  isDisplayTime?: boolean;

  //exclude column to not have thousand separator format
  //exclude column to not have thousand separator format
  columnsToNotHaveThousandSeparator?: Array<string>;
}

export const ModalBody: React.FC<ReadOnlyProps<Props>> = ({
  listData,
  excludedKeys,
  orderedKeys,
  renameList,
  specialLabels,
  columnsToNotHaveThousandSeparator,
  isDisplayTime = false,
}: ReadOnlyProps<Props>) => {
  const { t } = useTranslation();
  const { isDark } = useAppTheme();

  const translateValueText = (val: string) => {
    if (!val) return val;
    const v = val.toLowerCase();
    const knownKeys = ["shipped", "normal", "urgent", "scheduled", "all"];
    if (knownKeys.includes(v)) return t(v as any);
    return val;
  };

  const filteredContainerData =
    orderedKeys ||
    (
      Object.keys(listData).filter(
        (container) => !excludedKeys.includes(container)
      ) as Array<string>
    ).sort((a, b) => a.localeCompare(b));

  const items = filteredContainerData.map((value) => {
    const _value = ["", undefined, null].includes(listData[value])
      ? "N/A"
      : listData[value];

    const _filteredValue = columnsToNotHaveThousandSeparator?.includes(value)
      ? _value
      : formatValueAsDataType(_value, isDisplayTime);

    const _specialLabel =
      specialLabels && specialLabels[value] && specialLabels[value].newLabel;

    const _specialValue =
      specialLabels &&
      specialLabels[value] &&
      formatValueAsDataType(specialLabels[value].newValue, isDisplayTime);

    const _supportingValue =
      specialLabels &&
      specialLabels[value] &&
      formatValueAsDataType(
        specialLabels[value].supportingValue,
        isDisplayTime
      );

    const _formattedLabel =
      _specialLabel !== "" && _specialLabel !== undefined
        ? _specialLabel
        : renameList && renameList[value]
          ? renameList[value].name
          : renameLabel(value);

    return {
      key: value,
      label: _formattedLabel,
      valueText: `${_specialValue || _filteredValue} ${_supportingValue ? `(${_supportingValue})` : ""}`.trim(),
    };
  });

  const getGroup = (key: string, label: string) => {
    const k = (key + " " + label).toLowerCase();
    if (k.match(/date|time|eta|etd|loading|lodgement/)) return "Dates & Timing";
    if (k.match(/number|id|bl|dd|coo|po|code/)) return "Identifiers";
    if (k.match(/status|progress|priority|state|deliver/)) return "Status & Progress";
    if (k.match(/port|importer|size|type|vessal|weight|qty|measurement|remarks|country/)) return "Other Information";
    return "Other Information";
  };

  const groupedItems = items.reduce((acc, item) => {
    const group = getGroup(item.key, item.label as string);
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {} as Record<string, typeof items>);

  Object.keys(groupedItems).forEach((key) => {
    groupedItems[key].sort((a, b) => {
      const getWeight = (item: any) => {
        const k = (item.key + " " + item.label).toLowerCase();
        if (k.match(/priority|status|etd/)) return -1;
        if (k.includes("eta port") || k.includes("eta_port")) return 1;
        return 0;
      };
      const diff = getWeight(a) - getWeight(b);
      if (diff !== 0) return diff;
      return (a.key as string).localeCompare(b.key as string);
    });
  });

  // Sort groups in a logical order
  const groupOrder = ["Identifiers", "Status & Progress", "Dates & Timing", "Other Information"];
  const sortedGroups = Object.keys(groupedItems).sort((a, b) => {
    return groupOrder.indexOf(a) - groupOrder.indexOf(b);
  });

  return (
    <View style={{ paddingBottom: 16 }}>
      {sortedGroups.map((groupName) => (
        <View key={groupName} style={{ marginBottom: 16 }}>
          <View
            style={{
              backgroundColor: isDark ? "#2A2B2E" : "#F8FAFC",
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 6,
              marginBottom: 4,
            }}
          >
            <Text
              style={{
                fontSize: 13,
                color: isDark ? "#9CA3AF" : "#475569",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t(String(groupName).toLowerCase() as any)}
            </Text>
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
            {groupedItems[groupName].map((item) => (
              <View
                key={item.key}
                style={{
                  width: "50%",
                  paddingVertical: 12,
                  paddingHorizontal: 8,
                  borderBottomWidth: 1,
                  borderBottomColor: isDark ? "#374151" : "#F1F5F9",
                }}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: isDark ? "#9CA3AF" : "#64748B",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    marginBottom: 6,
                    letterSpacing: 0.5,
                  }}
                >
                  {t(String(item.label).toLowerCase() as any)}
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    color: isDark ? "#F9FAFB" : "#0F172A",
                    fontWeight: "500",
                  }}
                >
                  {translateValueText(String(item.valueText))}
                </Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};
